import Decimal from 'decimal.js';
import { compact } from 'lodash';

import { FUNCTIONAL } from '@/styles/tokens/colors';
import { formatCurrencyNoDecimals } from '@/utils/formatting/currency';

import { EstateWaterfall_TransferLogItemFragment } from '../../../graphql/EstateWaterfall.generated';
import { EdgeLabelCard } from '../edgeLabelCard.component';
import { CardConfig } from './types';
import { getTrustTermDisplay } from './utils';

interface CrtRemainderCardParams {
  sourceEntityName: string;
  targetEntityName: string;
  remainder?: EstateWaterfall_TransferLogItemFragment;
  crtFunding?: EstateWaterfall_TransferLogItemFragment;
  associatedIncomePayments?: EstateWaterfall_TransferLogItemFragment[];
}

export function createCrtRemainderCard({
  sourceEntityName,
  targetEntityName,
  remainder,
  crtFunding,
  associatedIncomePayments = [],
}: CrtRemainderCardParams): CardConfig {
  const crtName = sourceEntityName;
  const charity = targetEntityName;
  const isHypothetical = remainder?.isHypothetical;

  const fundingAmount = (() => {
    return crtFunding?.finalTransferAmount;
  })();

  // Calculate total income payments
  const totalIncomePayments = associatedIncomePayments.reduce(
    (sum, payment) => sum.plus(payment.finalTransferAmount || 0),
    new Decimal(0)
  );

  let year: number | null | undefined = remainder?.year;

  if (
    remainder?.associatedObject?.__typename === 'Entity' &&
    remainder.associatedObject.subtype?.__typename === 'CRTTrust'
  ) {
    const { termDurationLifetime } = remainder.associatedObject.subtype;
    if (termDurationLifetime) {
      year = null;
    } else {
      year = remainder.associatedObject.subtype.termEndDate?.getFullYear();
    }
  }

  const term = (() => {
    if (remainder?.associatedObject?.__typename === 'HypotheticalCRT') {
      const termYears =
        remainder.associatedObject.simulatedCrt.crtProposal?.termYears;
      return termYears ? `${termYears} years` : null;
    }

    if (
      remainder?.associatedObject?.__typename === 'Entity' &&
      remainder.associatedObject.subtype?.__typename === 'CRTTrust'
    ) {
      const { termDurationLifetime, termDurationYears } =
        remainder.associatedObject.subtype;
      return getTrustTermDisplay({ termDurationLifetime, termDurationYears });
    }

    return null;
  })();

  // Get remainder value
  const remainderValue = remainder?.finalTransferAmount || new Decimal(0);

  return {
    element: (
      <EdgeLabelCard
        key={`crt-remainder-${sourceEntityName}-${targetEntityName}`}
        title={isHypothetical ? 'Hypothetical CRT remainder' : 'CRT remainder'}
        titleSx={
          isHypothetical ? { color: FUNCTIONAL.WARNING[600] } : undefined
        }
        description={`Remaining balance from ${crtName} distributed to ${charity} at term end.`}
        rows={compact([
          fundingAmount
            ? {
                label: 'Funding amount',
                value: formatCurrencyNoDecimals(fundingAmount),
              }
            : null,
          totalIncomePayments.gt(0)
            ? {
                label: 'Total income payments',
                value: formatCurrencyNoDecimals(totalIncomePayments),
              }
            : null,
          term
            ? {
                label: 'CRT term',
                value: term,
              }
            : null,
          year
            ? {
                label: 'Term end',
                value: year.toString(),
              }
            : null,
          remainderValue.gt(0)
            ? {
                label: 'Remainder',
                value: formatCurrencyNoDecimals(remainderValue),
              }
            : null,
        ])}
      />
    ),
    label: 'CRT remainder',
    value: formatCurrencyNoDecimals(remainderValue),
    variant: isHypothetical ? 'secondary' : 'primary',
  };
}
